import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, Inbox, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, domaineLabel, errMsg, fmtDate, statutLabel, URGENCES } from "@/lib/api";
import { marquerSuiviVu } from "@/lib/suiviBadge";

const STATUT_STYLE = {
  recu: "bg-slate-100 text-slate-700 border-slate-200",
  qualifie: "bg-blue-50 text-blue-700 border-blue-200",
  planifie: "bg-indigo-50 text-indigo-700 border-indigo-200",
  en_cours: "bg-amber-50 text-amber-700 border-amber-200",
  resolu: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export const StatutBadge = ({ statut }) => (
  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${STATUT_STYLE[statut] || STATUT_STYLE.recu}`}>{statutLabel(statut)}</span>
);

export default function Suivi() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/tickets").then(({ data }) => {
      setTickets(data);
      setLoading(false);
      marquerSuiviVu(data);
    });
  }, []);

  const supprimer = async (t) => {
    if (!window.confirm(`Supprimer définitivement le ticket ${t.reference} ? Cette action est irréversible.`)) return;
    try {
      await api.delete(`/tickets/${t.id}`);
      setTickets((prev) => prev.filter((x) => x.id !== t.id));
      toast.success(`Ticket ${t.reference} supprimé`);
    } catch (e) {
      toast.error(errMsg(e, "Suppression impossible"));
    }
  };

  return (
    <div className="space-y-8" data-testid="suivi-page">
      <div>
        <div className="bk-label mb-2">Mes déclarations</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter text-slate-900">Suivi des tickets</h1>
      </div>

      {loading && <div className="text-slate-400 text-sm">Chargement…</div>}

      {!loading && tickets.length === 0 && (
        <div className="rounded-2xl border border-dashed border-slate-300 p-10 text-center" data-testid="suivi-empty">
          <Inbox size={26} className="mx-auto text-slate-300 mb-3" />
          <p className="text-sm text-slate-500">Aucun ticket pour le moment.</p>
        </div>
      )}

      <div className="space-y-3">
        {tickets.map((t, i) => {
          const urgence = URGENCES.find((u) => u.key === t.urgence);
          return (
            <motion.div key={t.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }} className="relative">
              <Link
                to={`/suivi/${t.id}`}
                data-testid={`ticket-row-${t.reference}`}
                className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white px-4 py-4 pr-24 hover:border-slate-400 transition-colors"
              >
                <span className="h-9 w-1.5 rounded-full shrink-0" style={{ background: urgence?.color }} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono-bk text-xs text-slate-500">{t.reference}</span>
                    <StatutBadge statut={t.statut} />
                    {t.resolu_automatiquement && <span className="text-xs text-emerald-700">auto-résolu</span>}
                  </div>
                  <div className="text-sm font-semibold text-slate-900 mt-1 truncate">
                    {domaineLabel(t.domaine)} · {t.site_nom || "Site principal"}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">{fmtDate(t.created_at)}</div>
                  {t.statut !== "resolu" && t.prise_en_charge_label && (
                    <div className="text-xs text-[#005CFF] font-semibold mt-1" data-testid={`prise-en-charge-${t.reference}`}>
                      Prise en charge {t.prise_en_charge_label}
                    </div>
                  )}
                </div>
                <ChevronRight size={16} className="text-slate-400 shrink-0 absolute right-4" />
              </Link>
              <button
                data-testid={`ticket-delete-${t.reference}`}
                onClick={() => supprimer(t)}
                title="Supprimer ce ticket"
                className="absolute right-12 top-1/2 -translate-y-1/2 p-2 rounded-lg text-slate-300 hover:text-red-600 hover:bg-red-50 transition-colors"
              >
                <Trash2 size={16} />
              </button>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
